import { errors } from '../../../services/ruutsApi/errors'
import type { ExportSheet, SOCSampleExport } from './buildSOCSampleExport'

const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const MIN_COLUMN_WIDTH = 10
const MAX_COLUMN_WIDTH = 40

type ExcelJSModule = typeof import('exceljs')

// Dynamically imported to keep exceljs out of the main bundle. Its browser build is UMD, so the
// bundler may expose the namespace on `default` rather than as named exports.
const loadExcelJS = async (): Promise<ExcelJSModule> => {
  let exceljs: ExcelJSModule
  try {
    exceljs = await import('exceljs')
  } catch {
    // A tab opened before a deploy asks for a chunk the new build no longer serves, and the
    // browser caches the failed fetch, so only a reload helps. A business error drops the
    // global toast's "intenta nuevamente" suffix.
    throw new errors.BusinessEntityError({
      message:
        'Hay una nueva versión de la aplicación. Recargá la página para descargar el archivo.',
      needSupport: false,
    })
  }
  return (exceljs as unknown as { default?: ExcelJSModule }).default ?? exceljs
}

const columnWidths = (sheet: ExportSheet, headerInfo: SOCSampleExport['headerInfo']) =>
  sheet.columns.map(({ key, header }, index) => {
    const headerBlockTexts = headerInfo.map(({ label, value }) => [label, value][index] ?? '')
    const longest = Math.max(
      header.length,
      ...headerBlockTexts.map(text => text.length),
      ...sheet.rows.map(row => String(row[key] ?? '').length),
    )
    return Math.min(Math.max(longest + 2, MIN_COLUMN_WIDTH), MAX_COLUMN_WIDTH)
  })

export const writeSOCSampleWorkbook = async ({
  headerInfo,
  sheets,
}: SOCSampleExport): Promise<Blob> => {
  const { Workbook } = await loadExcelJS()
  const workbook = new Workbook()

  sheets.forEach((sheet, sheetIndex) => {
    const tableHeaderRow = headerInfo.length + 2
    const worksheet = workbook.addWorksheet(sheet.name, {
      views: [{ state: 'frozen', ySplit: tableHeaderRow }],
    })

    headerInfo.forEach(({ label, value }, index) => {
      const row = worksheet.getRow(index + 1)
      row.getCell(1).value = label
      row.getCell(1).font = { bold: true }
      row.getCell(2).value = value
    })

    worksheet.addTable({
      name: `Tabla${sheetIndex + 1}`,
      ref: `A${tableHeaderRow}`,
      headerRow: true,
      style: { theme: 'TableStyleMedium2', showRowStripes: true },
      columns: sheet.columns.map(({ header }) => ({ name: header, filterButton: true })),
      // A table without data rows produces a file Excel reports as corrupt.
      rows: sheet.rows.length
        ? sheet.rows.map(row => sheet.columns.map(({ key }) => row[key]))
        : [sheet.columns.map(() => null)],
    })

    columnWidths(sheet, headerInfo).forEach((width, index) => {
      worksheet.getColumn(index + 1).width = width
    })
  })

  const buffer = await workbook.xlsx.writeBuffer()
  return new Blob([buffer], { type: XLSX_MIME_TYPE })
}
